/**
 * Example bodies for the tests of both sides: Lagerlog can check that its answer has this shape,
 * SpeisePlan can feed them to its code without a Lagerlog. Every product and allergen id is one of
 * food's (tools/test/parts.test.ts in household-kit checks it).
 */
import type { ConnectRequest, ConnectResponse, StockResponse } from "./pantry.ts";
import type { CardUpdate, CardUpdateResponse, CardsRequest, CardsResponse, RecipeCard, RecipeIndex, RecipesConnectRequest, RecipesConnectResponse } from "./recipes.ts";
import type { CookedRequest, CookedResponse, ReservationsRequest, ReservationsResponse } from "./reservations.ts";
import type { Delivery, DeliveryResponse, PurchasesResponse, ShoppingConnectResponse } from "./shopping.ts";

/** As SpeisePlan sends it: the code as typed. */
export const EXAMPLE_CONNECT_REQUEST = { code: "4f7k 2qxm", household: { name: "Zurmühl" } } as const;

/** As the check makes it. */
export const EXAMPLE_CONNECT_REQUEST_CHECKED: ConnectRequest = { code: "4F7K-2QXM", household: { name: "Zurmühl" } };

export const EXAMPLE_CONNECT_RESPONSE: ConnectResponse = {
  token: "q3Jk9vXz0Lr7m2N8pT5wYb1cD4eF6gH0iJ2kL4mN6oP",
  household: { name: "Weber" },
};

/**
 * Household Weber: tomatoes from two articles, cream half reserved, eggs, pasta with and without a
 * best-before date, and a bottle of Grauburgunder marked for cooking, reserved for the risotto
 * (375 ml) and the wine sauce (200 ml), so 175 ml are available. Each article lies under its own
 * base product: Spaghetti under "spaghetti", not under "nudeln", as Lagerlog files them; wine by
 * its colour.
 */
export const EXAMPLE_STOCK: StockResponse = {
  household: { name: "Weber" },
  at: "2026-10-22T08:15:00.000Z",
  items: [
    {
      product: "ei",
      unit: "Stk.",
      qty: 8,
      available: 8,
      lots: [
        { qty: 2, bestBefore: "2026-10-25" },
        { qty: 6, bestBefore: "2026-11-02" },
      ],
      articles: [{ name: "Eier Freiland (10 Stk.)", qty: 8 }],
    },
    {
      product: "nudeln",
      unit: "g",
      qty: 1000,
      available: 1000,
      lots: [{ qty: 500, bestBefore: "2027-03-01" }, { qty: 500 }],
      articles: [{ name: "Hörnchennudeln (500 g)", qty: 1000 }],
    },
    {
      product: "passierte-tomaten",
      unit: "g",
      qty: 1200,
      available: 1200,
      lots: [
        { qty: 400, bestBefore: "2027-01-31" },
        { qty: 800, bestBefore: "2027-06-30" },
      ],
      articles: [
        { name: "Mutti Passata (400 g)", qty: 400 },
        { name: "Passata Bio (800 g)", qty: 800 },
      ],
    },
    {
      product: "sahne",
      unit: "ml",
      qty: 400,
      available: 200,
      lots: [{ qty: 400, bestBefore: "2026-10-28" }],
      articles: [{ name: "Schlagsahne 30 % (200 ml)", qty: 400 }],
    },
    {
      product: "spaghetti",
      unit: "g",
      qty: 500,
      available: 500,
      lots: [{ qty: 500, bestBefore: "2027-05-31" }],
      articles: [{ name: "Spaghetti n. 5 (500 g)", qty: 500 }],
    },
    {
      product: "weisswein",
      unit: "ml",
      qty: 750,
      available: 175,
      lots: [{ qty: 750 }],
      articles: [{ name: "Grauburgunder 2023 (0,75 l)", qty: 750 }],
    },
  ],
};

/** Nothing in the pantry yet: still a valid answer. */
export const EXAMPLE_STOCK_EMPTY: StockResponse = { household: { name: "Weber" }, at: "2026-10-22T08:15:00.000Z", items: [] };

/** The plan of one week in SpeisePlan. */
export const EXAMPLE_PLAN = "woche-2026-44";

const RISOTTO = { id: "r-123", title: "Risotto mit Pilzen" };
const SAUCE = { id: "r-456", title: "Weinschaumsoße" };

/** The week reserves: wine for both recipes, cream for the sauce, Parmesan that is not there. */
export const EXAMPLE_RESERVATIONS_REQUEST: ReservationsRequest = {
  items: [
    { product: "weisswein", unit: "ml", qty: 375, recipe: RISOTTO, day: "2026-10-29" },
    { product: "parmesan", unit: "g", qty: 50, recipe: RISOTTO, day: "2026-10-29" },
    { product: "weisswein", unit: "ml", qty: 200, recipe: SAUCE, day: "2026-10-31" },
    { product: "sahne", unit: "ml", qty: 200, recipe: SAUCE, day: "2026-10-31" },
  ],
};

/** VorratsBuch's answer: all held but the Parmesan, which goes to the shopping list. */
export const EXAMPLE_RESERVATIONS_RESPONSE: ReservationsResponse = {
  items: [
    { ...EXAMPLE_RESERVATIONS_REQUEST.items[0]!, reserved: 375, missing: 0, article: "Grauburgunder 2023 (0,75 l)" },
    { ...EXAMPLE_RESERVATIONS_REQUEST.items[1]!, reserved: 0, missing: 50 },
    { ...EXAMPLE_RESERVATIONS_REQUEST.items[2]!, reserved: 200, missing: 0, article: "Grauburgunder 2023 (0,75 l)" },
    { ...EXAMPLE_RESERVATIONS_REQUEST.items[3]!, reserved: 200, missing: 0, article: "Schlagsahne 30 % (200 ml)" },
  ],
};

/** The risotto is cooked; the Parmesan was bought meanwhile but not booked in, so it is not taken. */
export const EXAMPLE_COOKED_REQUEST: CookedRequest = {
  key: "risotto-r-123-2026-10-29",
  recipe: RISOTTO,
  day: "2026-10-29",
  items: [
    { product: "weisswein", unit: "ml", qty: 375 },
    { product: "parmesan", unit: "g", qty: 50 },
  ],
};

export const EXAMPLE_COOKED_RESPONSE: CookedResponse = {
  items: [
    { product: "weisswein", unit: "ml", qty: 375, taken: 375 },
    { product: "parmesan", unit: "g", qty: 50, taken: 0 },
  ],
};

/** The EinkaufsZettel's household Weber has released its weekly list to VorratsBuch. */
export const EXAMPLE_SHOPPING_CONNECT_RESPONSE: ShoppingConnectResponse = {
  token: "Zx8yW7vU6tS5rQ4pO3nM2lK1jI0hG9fE8dC7bA6zY5x",
  household: { name: "Weber" },
  lists: [{ id: "0199f2a0-7c1e-7b3a-9d2e-4f5a6b7c8d9e", name: "Wocheneinkauf", guests: false }],
};

/** The delivery's fixed key: one per pantry, the whole current need each time. */
export const EXAMPLE_STOCK_DELIVERY_KEY = "stock";

/** Two articles below their reorder point: milk and dishwasher tabs. */
export const EXAMPLE_STOCK_DELIVERY: Delivery = {
  kind: "stock",
  date: "2026-10-23",
  sender: "Sandra",
  items: [
    {
      key: "milch",
      name: "Milch",
      quantities: [{ qty: 2, unit: "Packung", text: "2 Packungen" }],
      estimated: false,
      organic: false,
      priority: "normal",
      note: "ab 1 Packung nachkaufen",
      dishes: [],
    },
    {
      key: "spuelmaschinentabs",
      name: "Spülmaschinentabs",
      quantities: [{ qty: 1, unit: "Packung", text: "1 Packung" }],
      estimated: false,
      organic: false,
      priority: "normal",
      note: "",
      dishes: [],
    },
  ],
};

export const EXAMPLE_DELIVERY_RESPONSE: DeliveryResponse = {
  list: { id: "0199f2a0-7c1e-7b3a-9d2e-4f5a6b7c8d9e", name: "Wocheneinkauf" },
  added: ["Spülmaschinentabs"],
  updated: ["Milch"],
  unchanged: [],
  kept: [],
  removed: [],
};

/** One finished purchase; the beer goes to KellerLog's inbox. `product` once the EinkaufsZettel has food. */
export const EXAMPLE_PURCHASES: PurchasesResponse = {
  purchases: [
    {
      id: 42,
      at: "2026-10-23T16:40:00.000Z",
      list: { id: "0199f2a0-7c1e-7b3a-9d2e-4f5a6b7c8d9e", name: "Wocheneinkauf" },
      by: "Tobias",
      items: [
        { name: "Milch", quantity: 2, unit: "Packung", organic: false, category: "milch", product: "milch" },
        { name: "Butter", quantity: 250, unit: "g", organic: true, category: "milch", product: "butter" },
        { name: "Bananen", quantity: 1, unit: "kg", organic: false, category: "obst", product: "banane" },
        { name: "Bier", quantity: 6, unit: "Flasche", organic: false, category: "getraenke", product: "bier" },
      ],
    },
  ],
  next: null,
};

/** As SpeiseKarte's server sends it: the code as the owner typed it. */
export const EXAMPLE_RECIPES_CONNECT_REQUEST = { code: "4f7k 2qxm", business: { name: "Tapas-Bar Lola" } } as const;

export const EXAMPLE_RECIPES_CONNECT_REQUEST_CHECKED: RecipesConnectRequest = { code: "4F7K-2QXM", business: { name: "Tapas-Bar Lola" } };

/** SpeisePlan's household of the bar's cook. */
export const EXAMPLE_RECIPES_CONNECT_RESPONSE: RecipesConnectResponse = {
  token: "Tb5nQ8rW2yE6uI0oP4aS7dF1gH3jK9lZ5xC8vB2nM6q",
  household: { name: "Lola" },
};

const RECIPE_URL = "https://speiseplan.example/#/rezept/";

/**
 * The household of Tapas-Bar Lola: "Tapas" lies in "Karte Herbst", the lunch dishes in
 * "Mittagstisch"; the flan lies in no collection (only "all recipes" takes it).
 */
export const EXAMPLE_RECIPE_INDEX: RecipeIndex = {
  household: { name: "Lola" },
  at: "2026-10-27T09:30:00.000Z",
  collections: [
    { id: 3, name: "Karte Herbst", parent: null },
    { id: 7, name: "Tapas", parent: 3 },
    { id: 9, name: "Mittagstisch", parent: null },
  ],
  recipes: [
    { id: "albondigas", rev: 12, title: "Albóndigas in Tomatensoße", collections: [7, 9], updatedAt: "2026-10-26T17:05:00.000Z" },
    { id: "flan", rev: 1, title: "Flan", collections: [], updatedAt: "2026-09-02T11:00:00.000Z" },
    { id: "gambas-pil-pil", rev: 7, title: "Gambas Pil Pil", collections: [7], updatedAt: "2026-10-20T10:12:00.000Z" },
    { id: "patatas-bravas", rev: 3, title: "Patatas Bravas", collections: [7], updatedAt: "2026-10-01T08:40:00.000Z" },
    { id: "tortilla-de-patatas", rev: 2, title: "Tortilla de Patatas", collections: [9], updatedAt: "2026-10-14T12:20:00.000Z" },
  ],
};

const GAMBAS: RecipeCard = {
  id: "gambas-pil-pil",
  rev: 7,
  title: "Gambas Pil Pil",
  menuText: "Garnelen in Knoblauchöl mit Chili, dazu Brot.",
  description: "Ein Klassiker aus dem Baskenland. Die Garnelen nur kurz im heißen Öl garen, sonst werden sie zäh.",
  allergens: { contains: ["gluten", "crustaceans"], likely: [], complete: true },
  diet: null,
  collections: [7],
  url: `${RECIPE_URL}gambas-pil-pil`,
  updatedAt: "2026-10-20T10:12:00.000Z",
};

/** SpeiseKarte asks for the four it does not have yet, and for the paella, deleted meanwhile. */
export const EXAMPLE_CARDS_REQUEST: CardsRequest = { ids: ["gambas-pil-pil", "patatas-bravas", "tortilla-de-patatas", "albondigas", "paella-mixta"] };

/**
 * Gambas with menu text; Patatas Bravas vegan; the tortilla without menu text, its description
 * only a suggestion; the albóndigas with likely allergens (the stock, the bread in the meat) and
 * one ingredient line nobody could judge.
 */
export const EXAMPLE_RECIPE_CARDS: CardsResponse = {
  cards: [
    GAMBAS,
    {
      id: "patatas-bravas",
      rev: 3,
      title: "Patatas Bravas",
      menuText: "Frittierte Kartoffeln mit scharfer Tomatensoße.",
      description: "",
      allergens: { contains: [], likely: [], complete: true },
      diet: "vegan",
      collections: [7],
      url: `${RECIPE_URL}patatas-bravas`,
      updatedAt: "2026-10-01T08:40:00.000Z",
    },
    {
      id: "tortilla-de-patatas",
      rev: 2,
      title: "Tortilla de Patatas",
      menuText: "",
      description: "Die spanische Kartoffeltortilla mit Zwiebeln, am besten lauwarm.",
      allergens: { contains: ["eggs"], likely: [], complete: true },
      diet: "vegetarian",
      collections: [9],
      url: `${RECIPE_URL}tortilla-de-patatas`,
      updatedAt: "2026-10-14T12:20:00.000Z",
    },
    {
      id: "albondigas",
      rev: 12,
      title: "Albóndigas in Tomatensoße",
      menuText: "Hackbällchen in Tomatensoße mit Paprika.",
      description: "Wie bei Oma in Sevilla.",
      allergens: { contains: ["eggs", "gluten"], likely: ["celery", "milk"], complete: false },
      diet: null,
      collections: [7, 9],
      url: `${RECIPE_URL}albondigas`,
      updatedAt: "2026-10-26T17:05:00.000Z",
    },
  ],
  missing: ["paella-mixta"],
};

/** The owner renamed the gambas in SpeiseKarte; it rests on the rev SpeiseKarte saw last. */
export const EXAMPLE_CARD_UPDATE: CardUpdate = { rev: 7, title: "Gambas al Ajillo", by: { name: "Lola" } };

/** 200: written, with the new rev. */
export const EXAMPLE_CARD_UPDATE_RESPONSE: CardUpdateResponse = {
  card: { ...GAMBAS, rev: 8, title: "Gambas al Ajillo", updatedAt: "2026-10-27T09:41:00.000Z" },
};

/** 409: meanwhile the menu text was changed in SpeisePlan; this is the current state. */
export const EXAMPLE_CARD_CONFLICT: CardUpdateResponse = {
  card: { ...GAMBAS, rev: 8, menuText: "Garnelen in Knoblauchöl mit Chili und Petersilie, dazu Brot.", updatedAt: "2026-10-27T09:38:00.000Z" },
};
